/* Owner-only Editors panel on the cookbook page.
 * Two sections:
 *  • Accepted editors — name + email + last edit + Revoke
 *  • Pending invites — email + access level + sent date + Resend / Cancel
 * Either section is hidden when empty; whole component returns null when both
 * are empty so the page stays clean.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { UserCircle, X as XIcon, PaperPlaneTilt, Clock } from "@phosphor-icons/react";

function timeAgo(iso) {
  if (!iso) return "no edits yet";
  const then = new Date(iso).getTime();
  if (!then) return "no edits yet";
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
}

export default function CookbookEditorsPanel({ cookbookId, onChange }) {
  const [editors, setEditors] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [ed, pi] = await Promise.all([
        api.get(`/cookbooks/${cookbookId}/co-editors`).then((r) => r.data),
        api.get(`/cookbooks/${cookbookId}/pending-invites`).then((r) => r.data),
      ]);
      setEditors(Array.isArray(ed) ? ed : []);
      setPending(Array.isArray(pi) ? pi : []);
    } catch (err) {
      // 403 is expected for non-owners — render nothing silently.
      // Surface anything else (network, 500) so the user isn't left wondering.
      if (err?.response?.status !== 403) {
        toast.error("Couldn't load editors — try refreshing.");
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh() is a stable closure; only re-fetch when cookbookId changes
  useEffect(() => { refresh(); }, [cookbookId]);

  const revoke = async (editor) => {
    if (!window.confirm(`Remove ${editor.name || editor.email} as a co-editor? They'll lose edit access immediately.`)) return;
    try {
      await api.post(`/cookbooks/${cookbookId}/revoke-coeditor`, { user_id: editor.user_id });
      toast.success(`${editor.name || editor.email} removed`);
      await refresh();
      onChange?.();
    } catch { toast.error("Couldn't remove — try again."); }
  };

  const resendInvite = async (inv) => {
    try {
      const { data } = await api.post(`/cookbooks/${cookbookId}/pending-invites/${inv.invite_id}/resend`);
      if (data.sent) toast.success(`Invite re-sent to ${inv.email}`);
      else if (data.mock) toast.success(`Invite re-prepared (email service in mock mode) — token reused.`);
      else toast.success(`Invite refreshed for ${inv.email}.`);
      await refresh();
    } catch { toast.error("Couldn't resend — try again."); }
  };

  const cancelInvite = async (inv) => {
    if (!window.confirm(`Cancel the pending invite to ${inv.email}? The email link will stop working.`)) return;
    try {
      await api.delete(`/cookbooks/${cookbookId}/pending-invites/${inv.invite_id}`);
      toast.success(`Invite to ${inv.email} cancelled.`);
      await refresh();
    } catch { toast.error("Couldn't cancel — try again."); }
  };

  if (loading) return null;
  if (editors.length === 0 && pending.length === 0) return null;

  return (
    <div className="card-recipe mb-6" data-testid="cookbook-editors-panel">
      {editors.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div>
              <p className="overline">Editors ({editors.length})</p>
              <p className="text-xs text-rf-ink-soft">People you&apos;ve invited to co-edit this cookbook.</p>
            </div>
          </div>
          <ul className="divide-y divide-rf-ink/10 mb-2">
            {editors.map((e) => (
              <li key={e.user_id} data-testid={`editor-row-${e.user_id}`} className="flex items-center gap-3 py-2.5">
                <UserCircle size={28} weight="duotone" className="text-rf-orange flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-rf-ink text-sm truncate">{e.name || e.email}</p>
                  <p className="text-[11px] text-rf-ink-soft truncate">
                    {e.email}
                    {" · "}
                    <span data-testid={`editor-last-${e.user_id}`}>last edit {timeAgo(e.last_edit_at)}</span>
                  </p>
                </div>
                <button
                  data-testid={`editor-revoke-${e.user_id}`}
                  onClick={() => revoke(e)}
                  className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-xs font-bold p-2 rounded hover:bg-red-50 transition-colors"
                  title="Revoke co-editor access"
                >
                  <XIcon size={14} weight="bold" /> Revoke
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {pending.length > 0 && (
        <div data-testid="cookbook-pending-invites" className={editors.length > 0 ? "mt-4 pt-4 border-t border-rf-ink/10" : ""}>
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div>
              <p className="overline">Pending invites ({pending.length})</p>
              <p className="text-xs text-rf-ink-soft">Email links that haven&apos;t been accepted yet.</p>
            </div>
          </div>
          <ul className="divide-y divide-rf-ink/10">
            {pending.map((inv) => (
              <li key={inv.invite_id} data-testid={`pending-row-${inv.invite_id}`} className="flex items-center gap-3 py-2.5">
                <Clock size={26} weight="duotone" className="text-rf-ink-soft flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-rf-ink text-sm truncate">{inv.email}</p>
                  <p className="text-[11px] text-rf-ink-soft truncate flex items-center gap-1.5 flex-wrap">
                    <span className={`chip !py-0 !px-1.5 !text-[10px] ${inv.kind === "co_editor" ? "!bg-blue-100 !text-blue-800" : "!bg-rf-cream-alt !text-rf-ink-soft"}`}>
                      {inv.kind === "co_editor" ? "Co-editor" : "View-only"}
                    </span>
                    <span>sent {timeAgo(inv.sent_at)}</span>
                    {inv.expired && <span className="text-red-600 font-bold">· expired</span>}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    data-testid={`pending-resend-${inv.invite_id}`}
                    onClick={() => resendInvite(inv)}
                    className="inline-flex items-center gap-1 text-xs font-bold p-2 rounded text-rf-ink hover:bg-rf-amber/30 transition-colors"
                    title="Resend invite email"
                  ><PaperPlaneTilt size={14} weight="bold" /> Resend</button>
                  <button
                    data-testid={`pending-cancel-${inv.invite_id}`}
                    onClick={() => cancelInvite(inv)}
                    className="inline-flex items-center gap-1 text-xs font-bold p-2 rounded text-red-600 hover:bg-red-50 transition-colors"
                    title="Cancel this invite"
                  ><XIcon size={14} weight="bold" /> Cancel</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
